import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Wallet, Pencil, Trash2, RotateCcw } from "lucide-react";
import { SectionCard } from "@/components/common/SectionCard";
import { EmptyState } from "@/components/common/EmptyState";
import { EntryModal } from "@/components/common/EntryModal";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { UpdatesHistory } from "@/components/product/UpdatesHistory";
import { budgetFields } from "./financeFields";
import { BUDGET_STATUS, CATEGORIES, gbp2 as gbp, finBadge } from "@/data/financeOptions";

const pct = (b) => (b.pct_used == null ? null : b.pct_used);

const BudgetBar = ({ b }) => {
  const p = pct(b);
  const over = p != null && p > 100;
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-xs text-[#5A6B8C]">
        <span>{p == null ? "No planned amount" : `${p}% used`}</span>
        <span className={b.remaining_amount < 0 ? "font-semibold text-[#DC2626]" : ""}>{b.remaining_amount < 0 ? `Over by ${gbp(-b.remaining_amount)}` : `${gbp(b.remaining_amount)} left`}</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-[#E4E9F3]">
        <div className={`h-full rounded-full ${over ? "bg-[#DC2626]" : "bg-[#C9A227]"}`} style={{ width: `${p == null ? 0 : Math.min(100, p)}%` }} />
      </div>
    </div>
  );
};

export const BudgetsPanel = ({ onChange }) => {
  const [sp, setSp] = useSearchParams();
  const openId = sp.get("budget");
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false); const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null); const [confirmId, setConfirmId] = useState(null);
  const [q, setQ] = useState(""); const [statusF, setStatusF] = useState("all"); const [catF, setCatF] = useState("all");
  const [showArchived, setShowArchived] = useState(false);

  const recordTo = (id) => { const p = new URLSearchParams(sp); p.set("budget", id); return `?${p.toString()}`; };
  const closeRecord = useCallback(() => { const p = new URLSearchParams(sp); p.delete("budget"); setSp(p); }, [sp, setSp]);

  const load = useCallback(async () => { try { setItems(await api.list("budgets")); } catch { setItems([]); } finally { setLoaded(true); } }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (detail) { const fresh = items.find((b) => b.id === detail.id); if (fresh) setDetail(fresh); } }, [items]); // eslint-disable-line
  const openedRef = useRef(null);
  useEffect(() => {
    if (!openId) { openedRef.current = null; setDetail((d) => (d ? null : d)); return; }
    if (!loaded || openedRef.current === openId) return;
    const b = items.find((x) => x.id === openId);
    if (b) { openedRef.current = openId; setDetail(b); }
    else { toast.error("That budget is unavailable — it may have been deleted."); closeRecord(); }
  }, [openId, loaded, items, closeRecord]);

  const openAdd = () => { setEditing({ period_type: "Monthly", category: "Marketing", status: "Draft", owner: "Company" }); setModalOpen(true); };
  const openEdit = (r) => { setDetail(null); setEditing(r); setModalOpen(true); };
  const save = async (v) => { if (v.id) { await api.update("budgets", v.id, v); toast.success("Saved"); } else { await api.create("budgets", v); toast.success("Budget added"); } await load(); onChange?.(); };
  const setStatus = async (r, status) => { await api.update("budgets", r.id, { status }); toast.success(`Marked ${status}`); await load(); onChange?.(); };
  const doDelete = async () => { await api.remove("budgets", confirmId); toast.success("Deleted"); setConfirmId(null); setDetail(null); await load(); onChange?.(); };

  const filtered = useMemo(() => {
    let rows = items.filter((r) => (showArchived ? r.status === "Archived" : r.status !== "Archived"));
    if (q) { const s = q.toLowerCase(); rows = rows.filter((r) => r.name.toLowerCase().includes(s)); }
    if (statusF !== "all") rows = rows.filter((r) => r.status === statusF);
    if (catF !== "all") rows = rows.filter((r) => r.category === catF);
    return rows;
  }, [items, q, statusF, catF, showArchived]);

  return (
    <>
      <SectionCard testid="budgets-card" title="Budgets"
        action={
          <div className="flex items-center gap-3">
            <label className="flex cursor-pointer items-center gap-2 text-xs font-semibold text-[#5A6B8C]">
              <Switch data-testid="budgets-show-archived" checked={showArchived} onCheckedChange={setShowArchived} />
              Show archived
            </label>
            <Button size="sm" data-testid="add-budget-btn" onClick={openAdd} className="rounded-full bg-[#1D4ED8] text-white hover:bg-[#1A45C0]"><Plus size={15} /> Add Budget</Button>
          </div>
        }>
        <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative flex-1"><Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#5A6B8C]" /><Input data-testid="budgets-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search budgets…" className="border-[#E4E9F3] bg-white pl-9" /></div>
          <div className="flex flex-wrap gap-2">
            <Sel testid="budgets-filter-status" v={statusF} on={setStatusF} label="Status" options={BUDGET_STATUS} />
            <Sel testid="budgets-filter-category" v={catF} on={setCatF} label="Category" options={CATEGORIES} />
          </div>
        </div>
        {items.length === 0 ? <EmptyState icon={Wallet} title="No budgets yet" description="Create a budget to track planned vs actual spend." actionLabel="Add budget" onAction={openAdd} /> : filtered.length === 0 ? (
          <p data-testid="budgets-empty-filtered" className="py-10 text-center text-sm text-[#5A6B8C]">{showArchived ? "No archived budgets." : "No active budgets match your filters."}</p>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="budgets-grid">
            {filtered.map((b) => (
              <Link key={b.id} to={recordTo(b.id)} data-testid={`open-budget-${b.id}`} className="block rounded-xl border border-[#E4E9F3] bg-white p-4 text-left transition-colors hover:border-[#C9A227]/50">
                <div className="mb-2 flex items-start justify-between gap-2">
                  <div><p className="font-medium text-[#14245F]">{b.name}</p><p className="text-xs text-[#5A6B8C]">{b.category} · {b.period_type}</p></div>
                  <StatusBadge status={finBadge(b.status)} label={b.status} />
                </div>
                <div className="mb-2 grid grid-cols-3 gap-2 text-xs">
                  <div><p className="text-[#5A6B8C]">Planned</p><p className="font-medium text-[#14245F]">{gbp(b.planned_amount)}</p></div>
                  <div><p className="text-[#5A6B8C]">Actual</p><p className="font-medium text-[#14245F]">{gbp(b.actual_amount)}</p></div>
                  <div><p className="text-[#5A6B8C]">Committed</p><p className="font-medium text-[#14245F]">{gbp(b.committed_amount)}</p></div>
                </div>
                <BudgetBar b={b} />
              </Link>
            ))}
          </div>
        )}
      </SectionCard>

      <EntryModal open={modalOpen} onOpenChange={setModalOpen} title={editing?.id ? "Edit Budget" : "Add Budget"} submitLabel={editing?.id ? "Save Changes" : "Add Budget"} fields={budgetFields()} initialValues={editing} onSubmit={save} />
      <ConfirmDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)} title="Delete this budget?" description="This budget will be permanently removed." onConfirm={doDelete} />

      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) closeRecord(); }}>
        <DialogContent data-testid="budget-detail" className="max-h-[92vh] max-w-2xl overflow-y-auto border-[#E4E9F3] bg-white">
          {detail && (<>
            <DialogHeader>
              <div className="flex items-center gap-2"><StatusBadge status={finBadge(detail.status)} label={detail.status} /><span className="text-xs text-[#5A6B8C]">{detail.category} · {detail.period_type}</span></div>
              <DialogTitle className="mt-2 font-heading text-xl text-[#14245F]">{detail.name}</DialogTitle>
              <DialogDescription className="text-[#5A6B8C]">{detail.start_date || "—"} → {detail.end_date || "—"}</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="budget-metrics">
              {[["Planned", detail.planned_amount], ["Actual", detail.actual_amount], ["Committed", detail.committed_amount], ["Remaining", detail.remaining_amount]].map(([l, v]) => (
                <div key={l} className="rounded-lg border border-[#E4E9F3] bg-white p-3"><p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#5A6B8C]">{l}</p><p className={`mt-0.5 font-heading text-lg ${l === "Remaining" && v < 0 ? "text-[#DC2626]" : "text-[#14245F]"}`}>{gbp(v)}</p></div>
              ))}
            </div>
            <div className="mt-4"><BudgetBar b={detail} /></div>
            <div className="mt-4 flex flex-wrap gap-2 border-t border-[#E4E9F3] pt-4">
              <Button data-testid="budget-edit" onClick={() => openEdit(detail)} className="rounded-full bg-[#1D4ED8] text-white hover:bg-[#1A45C0]"><Pencil size={15} /> Edit</Button>
              {detail.status === "Archived" && <Button variant="outline" data-testid="budget-restore" onClick={() => setStatus(detail, "Active")} className="border-[#0EA5A4]/40 text-[#0EA5A4] hover:bg-[#0EA5A4]/10"><RotateCcw size={15} /> Restore</Button>}
              {detail.status !== "Closed" && detail.status !== "Archived" && <Button variant="outline" data-testid="budget-close" onClick={() => setStatus(detail, "Closed")} className="border-[#E4E9F3]">Close</Button>}
              {detail.status !== "Archived" && <Button variant="outline" data-testid="budget-archive" onClick={() => setStatus(detail, "Archived")} className="border-[#E4E9F3]">Archive</Button>}
              <Button variant="outline" data-testid="budget-delete" onClick={() => setConfirmId(detail.id)} className="border-[#E4E9F3] text-[#DC2626] hover:bg-[#DC2626]/10"><Trash2 size={15} /> Delete</Button>
            </div>
            <div className="mt-4 border-t border-[#E4E9F3] pt-4"><UpdatesHistory relatedId={detail.id} relatedType="Budget" relatedName={detail.name} area="Finance" /></div>
          </>)}
        </DialogContent>
      </Dialog>
    </>
  );
};

const Sel = ({ testid, v, on, label, options }) => (
  <Select value={v} onValueChange={on}><SelectTrigger data-testid={testid} className="w-auto min-w-[110px] border-[#E4E9F3] bg-white"><SelectValue placeholder={label} /></SelectTrigger><SelectContent><SelectItem value="all">{label}: All</SelectItem>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select>
);
